/**
 * Persistence for the player's Tweak-panel choices (localStorage, best-effort):
 * the tuning sliders and the ADR-023 steering model.
 */

import { resolveTuning } from "../sim/profile";
import type { ControlScheme, Tuning } from "../sim/types";
import { isAppearanceThemeId, type AppearanceThemeId } from "../render/themes";

const KEY = "streetsense_tuning_v1";
const STEER_KEY = "streetsense_steer_v1";
const APPEARANCE_THEME_KEY = "streetsense_appearance_theme";

export function loadSavedTuning(): Tuning | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Tuning>;
    return resolveTuning(parsed);
  } catch {
    return null;
  }
}

export function saveTuning(t: Tuning): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(resolveTuning(t)));
    return true;
  } catch {
    return false;
  }
}


export function loadSavedAppearanceTheme(): AppearanceThemeId | null {
  try {
    const raw = localStorage.getItem(APPEARANCE_THEME_KEY);
    return raw !== null && isAppearanceThemeId(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function saveAppearanceTheme(id: AppearanceThemeId): void {
  try {
    localStorage.setItem(APPEARANCE_THEME_KEY, id);
  } catch {
    /* private mode: the choice simply does not persist */
  }
}

export function clearSavedTuning(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Saved steering model, or null to use the level's own default. */
export function loadSavedScheme(): ControlScheme | null {
  try {
    const raw = localStorage.getItem(STEER_KEY);
    return raw === "heading" || raw === "rate" ? raw : null;
  } catch {
    return null;
  }
}

export function saveScheme(s: ControlScheme): void {
  try {
    localStorage.setItem(STEER_KEY, s);
  } catch {
    /* ignore */
  }
}

export function clearSavedScheme(): void {
  try {
    localStorage.removeItem(STEER_KEY);
  } catch {
    /* ignore */
  }
}

/** Which viewpoint the player last chose (see Renderer.setPlayView). */
const VIEW_KEY = "streetsense_view";

export function loadSavedView(): "third" | "first" {
  try {
    return localStorage.getItem(VIEW_KEY) === "first" ? "first" : "third";
  } catch {
    return "third";
  }
}

export function saveView(view: "third" | "first"): void {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    /* private mode: the choice simply does not persist */
  }
}

/** Which machine the player drives, and which world it drives in (ADR-048). */
export type RobotModelChoice = "bulldog" | "alaska" | "box";
export type EnvThemeChoice = "suburban" | "downtown";
/**
 * Sky and weather the player has forced (ADR-058). `auto` means "whatever
 * the map declares", which for every map but the night and the wet-dusk one
 * is a clear day — so the default stays fine weather without throwing away
 * the two scenarios that exist to be dark and wet.
 */
export type SkyChoice = "auto" | "day" | "dusk" | "night";
export type WeatherChoice = "auto" | "clear" | "rain";

const ROBOT_KEY = "streetsense_robot";
const ENV_KEY = "streetsense_env";
const SKY_KEY = "streetsense_sky";
const MUTE_KEY = "streetsense_muted";
const WEATHER_KEY = "streetsense_weather";

export function loadSavedRobot(): RobotModelChoice | null {
  try {
    const raw = localStorage.getItem(ROBOT_KEY);
    return raw === "bulldog" || raw === "alaska" || raw === "box" ? raw : null;
  } catch {
    return null;
  }
}

export function saveRobot(r: RobotModelChoice): void {
  try {
    localStorage.setItem(ROBOT_KEY, r);
  } catch {
    // Private mode: the choice simply does not survive the session.
  }
}

export function loadSavedMuted(): boolean | null {
  try {
    const raw = localStorage.getItem(MUTE_KEY);
    return raw === "1" ? true : raw === "0" ? false : null;
  } catch {
    return null;
  }
}

export function saveMuted(on: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, on ? "1" : "0");
  } catch {
    // Private mode: the choice simply does not survive the session.
  }
}

export function loadSavedSky(): SkyChoice | null {
  try {
    const raw = localStorage.getItem(SKY_KEY);
    return raw === "auto" || raw === "day" || raw === "dusk" || raw === "night" ? raw : null;
  } catch {
    return null;
  }
}

export function saveSky(v: SkyChoice): void {
  try {
    localStorage.setItem(SKY_KEY, v);
  } catch {
    // Private mode: the choice simply does not survive the session.
  }
}

export function loadSavedWeather(): WeatherChoice | null {
  try {
    const raw = localStorage.getItem(WEATHER_KEY);
    return raw === "auto" || raw === "clear" || raw === "rain" ? raw : null;
  } catch {
    return null;
  }
}

export function saveWeather(v: WeatherChoice): void {
  try {
    localStorage.setItem(WEATHER_KEY, v);
  } catch {
    // Private mode: the choice simply does not survive the session.
  }
}

export function loadSavedEnv(): EnvThemeChoice | null {
  try {
    const raw = localStorage.getItem(ENV_KEY);
    return raw === "suburban" || raw === "downtown" ? raw : null;
  } catch {
    return null;
  }
}

export function saveEnv(e: EnvThemeChoice): void {
  try {
    localStorage.setItem(ENV_KEY, e);
  } catch {
    // As above.
  }
}
